<script setup lang="ts">
import { type PropType } from 'vue';
import GaiaCard from '../base/GaiaCard.vue';
import GaiaCollapse from '../base/GaiaCollapse.vue';
import GaiaRiskPill from '../base/GaiaRiskPill.vue';
import GaiaSectionHeader from '../base/GaiaSectionHeader.vue';
import GaiaStatusIndicator from '../base/GaiaStatusIndicator.vue';
import type { ReviewExtractedField, ReviewFieldGroup } from '@/services/review/extractedField';

/**
 * The review detail's extracted fields, grouped by evaluation answer: a tinted
 * strip per group ("High risk · 2"), then one collapsible row per field with its
 * risk pill and confirmed check in the header.
 *
 * The body of an open row comes from the `#field` slot, so the list stays
 * ignorant of what a field looks like inside — the insight grid's drawer reuses
 * the same body without the list around it.
 */

const props = defineProps({
  groups: {
    type: Array as PropType<ReviewFieldGroup[]>,
    required: true,
  },
  /** The open field's uid; `n-collapse` accordion mode keeps it to one. */
  expandedUid: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{
  /** A row opened — the detail view jumps its PDF to that field's page. */
  select: [field: ReviewExtractedField];
}>();

function handleExpanded(names: string[] | string | number | (string | number)[]): void {
  const open = Array.isArray(names) ? names.at(-1) : names;
  if (open === undefined) return;
  for (const group of props.groups) {
    const field = group.fields.find(candidate => candidate.uid === String(open));
    if (field) {
      emit('select', field);
      return;
    }
  }
}
</script>

<template>
  <n-flex vertical :size="14" :wrap="false">
    <gaia-card v-for="group in props.groups" :key="group.key" content-style="padding: 0">
      <gaia-section-header variant="strip" :count="String(group.fields.length)">
        {{ group.label }}
      </gaia-section-header>

      <!-- Both edges come from here, matching the theme's
           `Collapse.titlePadding` (`12px 0 0 0`). naive zeroes that padding on
           the first item (`&:first-child > header { padding-top: 0 }`) and
           never adds any below the last one, so without this the group's rows
           sit flush against the card top and bottom. -->
      <gaia-collapse
        accordion
        :expanded-names="props.expandedUid"
        :style="{ padding: '12px 16px' }"
        @update:expanded-names="handleExpanded"
      >
        <n-collapse-item v-for="field in group.fields" :key="field.uid" :name="field.uid">
          <template #header>
            {{ field.field }}
          </template>
          <template #header-extra>
            <n-flex align="center" :size="8" :wrap="false">
              <gaia-risk-pill v-if="group.level !== 'none'" :level="group.level" pill>
                {{ group.label }}
              </gaia-risk-pill>
              <!-- The mock's own status slot: confirmed at a glance, without
                   opening the row. -->
              <gaia-status-indicator
                v-if="field.approved"
                status="success"
                glyph="check"
                title="Confirmed"
              />
            </n-flex>
          </template>

          <slot name="field" :field="field" />
        </n-collapse-item>
      </gaia-collapse>
    </gaia-card>
  </n-flex>
</template>
